import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Check, X, PencilSimple } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

const ATTENDANCE_COLUMNS = [
  { key: 'pilot_name', label: 'Pilot' },
  { key: 'date', label: 'Date' },
  { key: 'check_in', label: 'First Check In', format: r => r.check_in ? new Date(r.check_in).toLocaleString() : '' },
  { key: 'check_out', label: 'Last Check Out', format: r => r.check_out ? new Date(r.check_out).toLocaleString() : '' },
  { key: 'current_status', label: 'Status' },
  {
    key: 'sessions_count',
    label: 'Sessions',
    format: r => 1 + (Array.isArray(r.sessions) ? r.sessions.length : 0),
  },
  {
    key: 'active_minutes',
    label: 'Active Minutes',
    format: r => {
      const parts = [];
      (r.sessions || []).forEach(s => { if (s.check_in && s.check_out) parts.push([s.check_in, s.check_out]); });
      if (r.check_in) parts.push([r.check_in, r.check_out || null]);
      let total = 0;
      parts.forEach(([ci, co]) => {
        const start = new Date(ci).getTime();
        const end = co ? new Date(co).getTime() : Date.now();
        if (end > start) total += (end - start) / 60000;
      });
      return Math.round(total);
    },
  },
];
const BREAK_COLUMNS = [
  { key: 'pilot_name', label: 'Pilot' },
  { key: 'break_type', label: 'Type' },
  { key: 'date', label: 'Date' },
  { key: 'start_time', label: 'Start', format: r => r.start_time ? new Date(r.start_time).toLocaleString() : '' },
  { key: 'end_time', label: 'End', format: r => r.end_time ? new Date(r.end_time).toLocaleString() : '' },
  { key: 'duration_minutes', label: 'Duration (min)' },
  { key: 'edited_minutes', label: 'Edited (min)' },
  { key: 'status', label: 'Status' },
  { key: 'reason', label: 'Reason' },
];
const FUEL_COLUMNS = [
  { key: 'pilot_name', label: 'Pilot' },
  { key: 'vehicle_number', label: 'Vehicle' },
  { key: 'date', label: 'Date' },
  { key: 'fuel_amount', label: 'Litres' },
  { key: 'fuel_cost', label: 'Cost (₹)' },
  { key: 'odometer_reading', label: 'Odometer' },
  { key: 'fuel_station', label: 'Station' },
  { key: 'approval_status', label: 'Approval' },
];
const GEOFENCE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'radius_meters', label: 'Radius (m)' },
  { key: 'center_lat', label: 'Center Lat' },
  { key: 'center_lng', label: 'Center Lng' },
  { key: 'status', label: 'Status', format: r => r.status || 'active' },
];

export function AttendanceReport() {
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({ pilot_name: '', from_date: '', to_date: '' });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchRecords(); }, []);
  const fetchRecords = async () => {
    const params = {};
    if (filters.pilot_name) params.pilot_name = filters.pilot_name;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    try { const { data } = await api.get('/admin/attendance', { params }); setRecords(data); } catch {}
  };

  // Build an ordered list of { check_in, check_out } sessions for a record.
  // Prior sessions sit in `sessions[]`; the current pair lives on the doc itself
  // (check_out may be null if the pilot is actively checked in).
  const buildSessions = (r) => {
    const list = (r.sessions || []).map(s => ({ check_in: s.check_in, check_out: s.check_out }));
    if (r.check_in) list.push({ check_in: r.check_in, check_out: r.check_out || null });
    return list.sort((a, b) => (new Date(a.check_in || 0) - new Date(b.check_in || 0)));
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--';
  const sessionMinutes = (s) => {
    if (!s.check_in) return 0;
    const end = s.check_out ? new Date(s.check_out).getTime() : Date.now();
    const start = new Date(s.check_in).getTime();
    return Math.max(0, Math.round((end - start) / 60000));
  };

  return (
    <div data-testid="attendance-report" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Attendance Report</h1>
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-3 gap-4">
          <div><Label className="text-xs uppercase tracking-widest">Pilot Name</Label><Input value={filters.pilot_name} onChange={e => setFilters({...filters, pilot_name: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">From Date</Label><Input type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">To Date</Label><Input type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})} className="rounded-none" /></div>
        </div>
        <div className="flex justify-end mt-3 gap-2"><ExportButton rows={records} columns={ATTENDANCE_COLUMNS} filename="attendance" testId="export-attendance-btn" /><Button onClick={fetchRecords} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">Apply</Button></div>
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest w-8"></TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">First In</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Last Out</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Sessions</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Active (min)</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.map((r, i) => {
              const sessions = buildSessions(r);
              const isOpen = expanded === r.id;
              const firstIn = sessions[0]?.check_in || r.check_in;
              const lastOut = [...sessions].reverse().find(s => s.check_out)?.check_out || null;
              const activeMin = sessions.reduce((a, s) => a + sessionMinutes(s), 0);
              return (
                <React.Fragment key={r.id}>
                  <TableRow
                    className={`cursor-pointer ${i%2?'bg-[#F3F3F3]/30':''} hover:bg-[#002FA7]/5`}
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    data-testid={`attendance-row-${r.id}`}
                  >
                    <TableCell className="text-[#666] text-xs">{isOpen ? '▾' : '▸'}</TableCell>
                    <TableCell className="font-medium">{r.pilot_name}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{fmtTime(firstIn)}</TableCell>
                    <TableCell>{lastOut ? fmtTime(lastOut) : <span className="text-[#00C853] text-xs uppercase tracking-widest">Active</span>}</TableCell>
                    <TableCell className="text-right font-medium">{sessions.length}</TableCell>
                    <TableCell className="text-right font-medium">{activeMin}</TableCell>
                    <TableCell><span className="text-xs uppercase tracking-widest px-2 py-1 bg-[#002FA7]/10 text-[#002FA7]">{r.current_status}</span></TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-[#002FA7]/[0.03]">
                      <TableCell colSpan={8} className="p-4">
                        <div className="space-y-2" data-testid={`attendance-sessions-${r.id}`}>
                          <p className="text-xs uppercase tracking-widest font-medium text-[#666]">Sessions ({sessions.length})</p>
                          {sessions.length === 0 ? (
                            <p className="text-xs text-[#666]">No sessions recorded</p>
                          ) : (
                            <div className="space-y-1">
                              {sessions.map((s, idx) => {
                                const mins = sessionMinutes(s);
                                const active = !s.check_out;
                                return (
                                  <div key={idx} className="flex items-center gap-3 text-sm font-mono">
                                    <span className="inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold bg-[#002FA7] text-white">{idx + 1}</span>
                                    <span className="text-[#00C853]">⬆ {fmtTime(s.check_in)}</span>
                                    <span className="text-[#666]">→</span>
                                    <span className={active ? 'text-[#00C853]' : 'text-[#FF3333]'}>
                                      {active ? 'ACTIVE' : `⬇ ${fmtTime(s.check_out)}`}
                                    </span>
                                    <span className="text-[#666] text-xs">({mins} min{active ? ' so far' : ''})</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {records.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-8">No records</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function BreakApprovals() {
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({ break_type: '', pilot_name: '', from_date: '', to_date: '', status: '' });
  const [editDialog, setEditDialog] = useState(null);
  const [editMinutes, setEditMinutes] = useState(0);

  useEffect(() => { fetchRecords(); }, []);
  const fetchRecords = async () => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if(v) params[k] = v; });
    try { const { data } = await api.get('/admin/break-records', { params }); setRecords(data); } catch {}
  };

  const handleAction = async (id, action, edited_minutes = null) => {
    await api.put(`/admin/break-records/${id}/approve`, { action, edited_minutes });
    fetchRecords();
    setEditDialog(null);
  };

  return (
    <div data-testid="break-approvals" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Break Approvals</h1>
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div><Label className="text-xs uppercase tracking-widest">Type</Label>
            <select className="w-full border border-[#E5E5E5] p-2 text-sm" value={filters.break_type} onChange={e => setFilters({...filters, break_type: e.target.value})}>
              <option value="">All</option><option value="breakfast">Breakfast</option><option value="lunch">Lunch</option><option value="dinner">Dinner</option><option value="permission">Permission</option><option value="mgmt_work">Mgmt Work</option>
            </select>
          </div>
          <div><Label className="text-xs uppercase tracking-widest">Pilot</Label><Input value={filters.pilot_name} onChange={e => setFilters({...filters, pilot_name: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">From</Label><Input type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">To</Label><Input type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Status</Label>
            <select className="w-full border border-[#E5E5E5] p-2 text-sm" value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
              <option value="">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-3 gap-2"><ExportButton rows={records} columns={BREAK_COLUMNS} filename="break_approvals" testId="export-breaks-btn" /><Button onClick={fetchRecords} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">Apply</Button></div>
      </div>

      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit & Approve</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm">Excess: <strong>{editDialog?.excess_minutes} min</strong></p>
            <div><Label className="text-xs uppercase tracking-widest">Approved Minutes</Label><Input type="number" value={editMinutes} onChange={e => setEditMinutes(parseInt(e.target.value)||0)} className="rounded-none" /></div>
            <p className="text-sm text-[#666]">Rejected: {(editDialog?.excess_minutes || 0) - editMinutes} min</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialog(null)} className="rounded-none">Cancel</Button>
              <Button onClick={() => handleAction(editDialog.id, 'edit_approve', editMinutes)} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">Approve</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Duration</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Excess</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.map((r, i) => (
              <TableRow key={r.id} className={i%2?'bg-[#F3F3F3]/30':''}>
                <TableCell className="font-medium">{r.pilot_name}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell><span className="text-xs uppercase tracking-widest">{r.break_type}</span></TableCell>
                <TableCell className="text-right">{r.duration_minutes} min</TableCell>
                <TableCell className="text-right font-medium">{r.excess_minutes > 0 ? <span className="text-[#FF3333]">{r.excess_minutes} min</span> : '-'}</TableCell>
                <TableCell><span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.approval_status === 'approved' ? 'bg-[#00C853]/10 text-[#00C853]' : r.approval_status === 'rejected' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#FFD700]/10 text-[#B8860B]'}`}>{r.approval_status}</span></TableCell>
                <TableCell className="text-right">
                  {r.approval_status === 'pending' && r.excess_minutes > 0 && (
                    <>
                      <button onClick={() => handleAction(r.id, 'approve')} className="p-1 hover:bg-[#00C853]/10 text-[#00C853]" title="Approve"><Check size={16} weight="bold" /></button>
                      <button onClick={() => { setEditDialog(r); setEditMinutes(Math.floor(r.excess_minutes / 2)); }} className="p-1 hover:bg-[#002FA7]/10 text-[#002FA7]" title="Edit & Approve"><PencilSimple size={16} /></button>
                      <button onClick={() => handleAction(r.id, 'reject')} className="p-1 hover:bg-[#FF3333]/10 text-[#FF3333]" title="Reject"><X size={16} weight="bold" /></button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-[#666] py-8">No break records</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function FuelReport() {
  const [records, setRecords] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ pilot_name: '', vehicle_number: '', from_date: '', to_date: '', supplier_id: '' });
  const [exporting, setExporting] = useState('');

  useEffect(() => { fetchRecords(); fetchSuppliers(); }, []);
  const fetchSuppliers = async () => {
    try { const { data } = await api.get('/admin/fuel-suppliers'); setSuppliers(data); } catch {}
  };
  const fetchRecords = async () => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if(v) params[k] = v; });
    try { const { data } = await api.get('/admin/fuel', { params }); setRecords(data); } catch {}
  };

  const handleApprove = async (id, editedKm = null) => {
    const body = {};
    if (editedKm !== null) body.edited_km = editedKm;
    await api.put(`/admin/fuel/${id}/approve`, body);
    fetchRecords();
  };

  const exportFile = async (kind) => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if(v) params[k] = v; });
    setExporting(kind);
    try {
      const { data } = await api.get(`/admin/fuel/export-${kind}`, { params });
      if (data?.url) {
        const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
        window.open(base + data.url, '_blank');
      }
    } catch (e) {
      alert(`Export failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setExporting('');
    }
  };

  const totals = records.reduce((acc, r) => ({
    liters: acc.liters + (parseFloat(r.fuel_amount) || 0),
    amount: acc.amount + (parseFloat(r.fuel_cost) || 0),
  }), { liters: 0, amount: 0 });

  return (
    <div data-testid="fuel-report" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Fuel Reports</h1>
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div><Label className="text-xs uppercase tracking-widest">Fuel Supplier</Label>
            <select
              value={filters.supplier_id}
              onChange={e => setFilters({...filters, supplier_id: e.target.value})}
              className="w-full h-10 px-3 border border-[#E5E5E5] rounded-none bg-white text-sm"
              data-testid="fuel-filter-supplier">
              <option value="">All Suppliers</option>
              {suppliers.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
          </div>
          <div><Label className="text-xs uppercase tracking-widest">Pilot</Label><Input data-testid="fuel-filter-pilot" value={filters.pilot_name} onChange={e => setFilters({...filters, pilot_name: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Vehicle</Label><Input data-testid="fuel-filter-vehicle" value={filters.vehicle_number} onChange={e => setFilters({...filters, vehicle_number: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">From</Label><Input data-testid="fuel-filter-from" type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">To</Label><Input data-testid="fuel-filter-to" type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})} className="rounded-none" /></div>
        </div>
        <div className="flex flex-wrap justify-between mt-3 gap-2 items-center">
          <div className="text-sm text-[#666]" data-testid="fuel-totals">
            <span className="font-bold text-[#0A0A0A]">{records.length}</span> records ·
            <span className="font-bold text-[#0A0A0A] ml-2">{totals.liters.toFixed(2)} L</span> ·
            <span className="font-bold text-[#0A0A0A] ml-2">₹{totals.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => exportFile('pdf')} disabled={exporting === 'pdf'} data-testid="export-fuel-pdf-btn"
              className="bg-[#D32F2F] text-white hover:bg-[#D32F2F]/90 hover:text-white rounded-none uppercase tracking-widest text-xs px-4">
              {exporting === 'pdf' ? 'Generating…' : 'Export PDF'}
            </Button>
            <Button onClick={() => exportFile('excel')} disabled={exporting === 'excel'} data-testid="export-fuel-excel-btn"
              className="bg-[#1B7F3A] text-white hover:bg-[#1B7F3A]/90 hover:text-white rounded-none uppercase tracking-widest text-xs px-4">
              {exporting === 'excel' ? 'Generating…' : 'Export Excel'}
            </Button>
            <Button onClick={fetchRecords} data-testid="fuel-apply-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">Apply</Button>
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Supplier</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">KM Reading</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Liters</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Bill Amount</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.map((r, i) => (
              <TableRow key={r.id} className={i%2?'bg-[#F3F3F3]/30':''}>
                <TableCell className="font-medium">{r.pilot_name}</TableCell>
                <TableCell>{r.vehicle_number}</TableCell>
                <TableCell>{r.fuel_supplier_name || '—'}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell className="text-right">{r.km_reading}</TableCell>
                <TableCell className="text-right">{r.fuel_amount} L</TableCell>
                <TableCell className="text-right">₹{r.fuel_cost}</TableCell>
                <TableCell><span className={`text-xs uppercase tracking-widest px-2 py-1 ${r.approval_status === 'approved' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FFD700]/10 text-[#B8860B]'}`}>{r.approval_status}</span></TableCell>
                <TableCell className="text-right">
                  {r.approval_status === 'pending' && (
                    <button onClick={() => handleApprove(r.id)} className="p-1 hover:bg-[#00C853]/10 text-[#00C853]" title="Approve" data-testid={`approve-fuel-${r.id}`}><Check size={16} weight="bold" /></button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-[#666] py-8">No fuel records</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function GeofenceManagement() {
  const [fences, setFences] = useState([]);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => { fetchFences(); }, []);
  const fetchFences = async () => { try { const { data } = await api.get('/admin/geofences'); setFences(data); } catch {} };

  const handleToggle = async (id) => {
    await api.put(`/admin/toggle-status/geofences/${id}`);
    fetchFences();
  };

  const GeofenceEditorLazy = React.lazy(() => import('../../components/GeofenceEditor'));

  return (
    <div data-testid="geofence-management" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Halt Locations</h1>
          <p className="text-[#666] text-sm mt-1">{fences.length} halt locations with geofences</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton rows={fences} columns={GEOFENCE_COLUMNS} filename="halt_locations" testId="export-geofences-btn" />
          <Button onClick={() => setShowEditor(!showEditor)} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="toggle-geofence-editor-btn">
            {showEditor ? 'Hide Map Editor' : 'Add Geofence'}
          </Button>
        </div>
      </div>

      {showEditor && (
        <React.Suspense fallback={<div className="h-[500px] bg-[#F3F3F3] flex items-center justify-center text-[#666] text-sm">Loading map editor...</div>}>
          <GeofenceEditorLazy existingFences={fences} onSave={() => { fetchFences(); }} />
        </React.Suspense>
      )}

      <div className="bg-white border border-[#E5E5E5]">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest">Halt Location</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Geofence Name</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Address</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Points</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {fences.map((f, i) => (
              <TableRow key={f.id} className={i%2?'bg-[#F3F3F3]/30':''}>
                <TableCell className="font-medium">{f.halt_location || '-'}</TableCell>
                <TableCell>{f.name}</TableCell>
                <TableCell className="text-sm text-[#666]">{f.address || '-'}</TableCell>
                <TableCell className="text-right">{f.polygon?.length || 0}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${(f.status || 'active') === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF3333]/10 text-[#FF3333]'}`}>
                    {f.status || 'active'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => handleToggle(f.id)} className={`p-2 transition-colors ${(f.status || 'active') === 'active' ? 'hover:bg-[#FF3333]/10 text-[#FF3333]' : 'hover:bg-[#00C853]/10 text-[#00C853]'}`} title={(f.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}>
                    <X size={16} />
                  </button>
                  <SuperAdminDeleteButton masterType="geofences" recordId={f.id} recordName={f.name} onDeleted={fetchFences} />
                </TableCell>
              </TableRow>
            ))}
            {fences.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#666] py-8">No geofences defined. Click "Add Geofence" to draw on the map.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ReportsPage() {
  const [tab, setTab] = useState('trips');
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ from_date: '', to_date: '', pilot_name: '', staff_name: '', vehicle_number: '', supplier_name: '' });

  const fetchReport = async () => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if(v) params[k] = v; });
    try {
      const endpoint = tab === 'trips' ? '/admin/reports/trip-summary' : '/admin/reports/pilot-performance';
      const res = await api.get(endpoint, { params });
      setData(res.data);
    } catch {}
  };

  useEffect(() => { fetchReport(); }, [tab]);

  return (
    <div data-testid="reports-page" className="space-y-6">
      <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Reports</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-[#F3F3F3] border border-[#E5E5E5]">
          <TabsTrigger value="trips" className="rounded-none text-xs uppercase tracking-widest">Trip Summary</TabsTrigger>
          <TabsTrigger value="performance" className="rounded-none text-xs uppercase tracking-widest">Pilot Performance</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="bg-white border border-[#E5E5E5] p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div><Label className="text-xs uppercase tracking-widest">From</Label><Input type="date" value={filters.from_date} onChange={e => setFilters({...filters, from_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">To</Label><Input type="date" value={filters.to_date} onChange={e => setFilters({...filters, to_date: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Pilot</Label><Input value={filters.pilot_name} onChange={e => setFilters({...filters, pilot_name: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Staff</Label><Input value={filters.staff_name} onChange={e => setFilters({...filters, staff_name: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Vehicle</Label><Input value={filters.vehicle_number} onChange={e => setFilters({...filters, vehicle_number: e.target.value})} className="rounded-none" /></div>
          <div><Label className="text-xs uppercase tracking-widest">Supplier</Label><Input value={filters.supplier_name} onChange={e => setFilters({...filters, supplier_name: e.target.value})} className="rounded-none" /></div>
        </div>
        <div className="flex justify-end mt-3 gap-2">
          <ExportButton
            rows={data}
            columns={tab === 'trips' ? [
              { key: 'id', label: 'ID' },
              { key: 'pickup_supplier_name', label: 'Pickup' },
              { key: 'drop_supplier_name', label: 'Drop' },
              { key: 'pilot_name', label: 'Pilot' },
              { key: 'vehicle_number', label: 'Vehicle' },
              { key: 'status', label: 'Status' },
              { key: 'priority', label: 'Priority' },
              { key: 'created_at', label: 'Created At' },
              { key: 'completed_at', label: 'Completed At' },
              { key: 'manual_kms', label: 'Manual KMs' },
              { key: 'google_kms', label: 'Google KMs' },
            ] : [
              { key: 'pilot_name', label: 'Pilot' },
              { key: 'date', label: 'Date' },
              { key: 'check_in', label: 'Check In', format: r => r.check_in ? new Date(r.check_in).toLocaleString() : '' },
              { key: 'check_out', label: 'Check Out', format: r => r.check_out ? new Date(r.check_out).toLocaleString() : '' },
              { key: 'current_status', label: 'Status' },
            ]}
            filename={tab === 'trips' ? 'trip_summary' : 'pilot_performance'}
            testId={`export-reports-${tab}-btn`}
          />
          <Button onClick={fetchReport} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">Generate</Button>
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E5] p-4">
        <p className="text-sm text-[#666] mb-2">{data.length} records found</p>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          {tab === 'trips' ? (
            <Table><TableHeader><TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">ID</TableHead><TableHead className="text-xs uppercase tracking-widest">Pickup</TableHead><TableHead className="text-xs uppercase tracking-widest">Drop</TableHead><TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead><TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead><TableHead className="text-xs uppercase tracking-widest">Priority</TableHead>
            </TableRow></TableHeader>
              <TableBody>{data.map((d, i) => (<TableRow key={d.id} className={i%2?'bg-[#F3F3F3]/30':''}><TableCell className="font-mono text-xs">{d.id?.slice(0,8)}</TableCell><TableCell>{d.pickup_supplier_name}</TableCell><TableCell>{d.drop_supplier_name}</TableCell><TableCell>{d.pilot_name||'-'}</TableCell><TableCell>{d.vehicle_number||'-'}</TableCell><TableCell className="text-xs uppercase">{d.status}</TableCell><TableCell className="text-xs uppercase">{d.priority}</TableCell></TableRow>))}</TableBody>
            </Table>
          ) : (
            <Table><TableHeader><TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead><TableHead className="text-xs uppercase tracking-widest">Date</TableHead><TableHead className="text-xs uppercase tracking-widest">Check In</TableHead><TableHead className="text-xs uppercase tracking-widest">Check Out</TableHead><TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            </TableRow></TableHeader>
              <TableBody>{data.map((d, i) => (<TableRow key={d.id} className={i%2?'bg-[#F3F3F3]/30':''}><TableCell className="font-medium">{d.pilot_name}</TableCell><TableCell>{d.date}</TableCell><TableCell>{d.check_in ? new Date(d.check_in).toLocaleTimeString() : '-'}</TableCell><TableCell>{d.check_out ? new Date(d.check_out).toLocaleTimeString() : '-'}</TableCell><TableCell className="text-xs uppercase">{d.current_status}</TableCell></TableRow>))}</TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
